/**
 * ExerciseTileCard - Square compact card for ExerciseVariation
 * Created by neo on 23.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { ExerciseVariation } from '../../Model/ProgramPortfolio/ExerciseVariation';
import { useLanguage } from '../../Hooks/useLanguage';
import { getMediaMedium } from '../../Model/Media/Media';
import { Theme } from '../../Theme/Theme';

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 20px;
  overflow: hidden;
  isolation: isolate;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-6px) rotate(-1deg);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .image {
      transform: scale(1.1);
    }

    .glass-overlay {
      background: rgba(0, 0, 0, 0.1);
    }

    .type-badge {
      transform: scale(1.05);
    }

    .play-icon {
      opacity: 1;
    }
  }
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image }) =>
    image ? `url('${image}')` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)'};
  background-size: cover;
  background-position: center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const GlassOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  transition: all 0.35s ease;
`;

const Content = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px;
  color: white;
`;

const TypeBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  background: ${Theme.Colors.primary};
  backdrop-filter: blur(8px);
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
  transition: transform 0.3s ease;
`;

const Title = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: white;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
`;

const PlayIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  &::after {
    content: '';
    border-style: solid;
    border-width: 10px 0 10px 16px;
    border-color: transparent transparent transparent white;
    margin-left: 4px;
  }
`;

export type ExerciseTileCardProps = {
  exercise: ExerciseVariation;
  onClick?: (exercise: ExerciseVariation) => void;
};

export const ExerciseTileCard: React.FC<ExerciseTileCardProps> = observer(({ exercise, onClick }) => {
  const { language } = useLanguage();

  const title = exercise.localeName(language);
  const imageMedium = exercise.firstSmallestMediaUrl || (exercise.imageMedia ? getMediaMedium(exercise.imageMedia) : undefined);
  const hasVideo = exercise.videoMedia !== undefined;

  const handleClick = React.useCallback(() => {
    onClick?.(exercise);
  }, [onClick, exercise]);

  return (
    <CardContainer onClick={handleClick}>
      <BackgroundImage className="image" image={imageMedium} />
      <GlassOverlay className="glass-overlay" />
      <TypeBadge className="type-badge">{exercise.type}</TypeBadge>
      {hasVideo && <PlayIcon className="play-icon" />}
      <Content>
        <Title>{title}</Title>
      </Content>
    </CardContainer>
  );
});
